import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import OrderDesign from '../../../OrderDesign';
import { API_URL } from '../../../config/apiConfig';
import {
  THE_GAMBLE_BACKGROUND_SRC,
  THE_GAMBLE_TARGET_SCORE,
  THE_GAMBLE_WIN_PICK_VALUE,
  THE_GAMBLE_LOSS_PICK_VALUE,
  theGamblePlayerDeskCardPercentPosition,
} from './theGambleLayout';
import './TheGambleMinigame.css';

/**
 * @param {object} props
 * @param {{ targetScore: number, winPickValue: number, lossPickValue: number, deck: { deckIndex: number, orderId: number, cost: number }[], openingDeckIndex: number }} props.challenge
 * @param {(pickedTreeIndex: number, opts?: { gambleDrawIndices?: number[], gambleBust?: boolean }) => void} props.onPickTree
 * @param {boolean} props.disabled
 * @param {React.RefObject<HTMLElement>} props.containerRef
 * @param {null | 'won' | 'lost'} props.resultOutcome
 * @param {(() => void) | undefined} props.onResultProceed
 * @param {{ id: string, xpGained?: number, darknessReductionApplied?: number, billyBucksAmount?: number, startHealthBonus?: number } | null} props.winReward
 * @param {{ playerTotal: number, grannyTotal: number | null, won: boolean, bust: boolean } | null} props.gambleResolution
 */
export default function TheGambleMinigame({
  challenge,
  onPickTree,
  disabled = false,
  containerRef,
  resultOutcome = null,
  onResultProceed,
  winReward = null,
  gambleResolution = null,
}) {
  const { t, i18n } = useTranslation();
  const playArenaRef = useRef(null);
  const submittedRef = useRef(false);

  const targetScore = challenge.targetScore ?? THE_GAMBLE_TARGET_SCORE;
  const winPickValue = challenge.winPickValue ?? THE_GAMBLE_WIN_PICK_VALUE;
  const lossPickValue = challenge.lossPickValue ?? THE_GAMBLE_LOSS_PICK_VALUE;
  const deck = challenge.deck;
  const openingDeckIndex = challenge.openingDeckIndex;

  const [drawIndices, setDrawIndices] = useState(() => [openingDeckIndex]);
  const [orderById, setOrderById] = useState({});
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [orderLoadError, setOrderLoadError] = useState(null);
  const [grannyRevealTotal, setGrannyRevealTotal] = useState(null);

  const setPlayArenaRef = useCallback(
    (node) => {
      playArenaRef.current = node;
      if (containerRef && typeof containerRef === 'object') {
        containerRef.current = node;
      }
    },
    [containerRef]
  );

  const remainingDeckIndices = useMemo(() => {
    const drawn = new Set(drawIndices);
    return deck.map((c) => c.deckIndex).filter((idx) => !drawn.has(idx));
  }, [deck, drawIndices]);

  const playerTotal = useMemo(() => {
    let sum = 0;
    for (const deckIndex of drawIndices) {
      const card = deck.find((c) => c.deckIndex === deckIndex);
      if (card) {
        sum += Number(card.cost);
      }
    }
    return sum;
  }, [deck, drawIndices]);

  const playerBust = playerTotal > targetScore;
  const canHit = !playerBust && resultOutcome == null && !disabled && remainingDeckIndices.length > 0;
  const canHold = !playerBust && resultOutcome == null && !disabled && drawIndices.length > 0;

  const winRewardBodyKey =
    resultOutcome === 'won' && winReward && typeof winReward.id === 'string'
      ? `exploreUnknownPage.minigame.theGamble.winReward.${winReward.id}`
      : null;

  const fetchOrderFull = useCallback(
    async (orderId) => {
      const id = Number(orderId);
      if (Number.isNaN(id)) {
        throw new Error(`TheGambleMinigame: invalid orderId (${String(orderId)})`);
      }
      const authToken = localStorage.getItem('token');
      const uiLang = encodeURIComponent(i18n.resolvedLanguage || i18n.language || 'en-us');
      const response = await fetch(`${API_URL}/order/${id}/full?lang=${uiLang}`, {
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch order ${id} (status ${response.status})`);
      }
      return response.json();
    },
    [i18n.resolvedLanguage, i18n.language]
  );

  useEffect(() => {
    if (!Array.isArray(drawIndices) || drawIndices.length < 1) {
      return;
    }
    let cancelled = false;
    const missingIds = [];
    for (const deckIndex of drawIndices) {
      const card = deck.find((c) => c.deckIndex === deckIndex);
      if (card && orderById[card.orderId] == null) {
        missingIds.push(card.orderId);
      }
    }
    if (missingIds.length === 0) {
      return undefined;
    }
    setOrdersLoading(true);
    setOrderLoadError(null);
    (async () => {
      const updates = {};
      for (const orderId of missingIds) {
        const full = await fetchOrderFull(orderId);
        if (cancelled) return;
        updates[orderId] = full;
      }
      if (!cancelled) {
        setOrderById((prev) => ({ ...prev, ...updates }));
        setOrdersLoading(false);
      }
    })().catch((err) => {
      if (!cancelled) {
        setOrderLoadError(err instanceof Error ? err.message : String(err));
        setOrdersLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [drawIndices, deck, fetchOrderFull, orderById]);

  useEffect(() => {
    if (gambleResolution && gambleResolution.grannyTotal != null) {
      setGrannyRevealTotal(Number(gambleResolution.grannyTotal));
    }
  }, [gambleResolution]);

  const submitOutcome = useCallback(
    (opts) => {
      if (submittedRef.current || disabled || resultOutcome != null) {
        return;
      }
      submittedRef.current = true;
      const indices = [...drawIndices];
      if (opts.bust) {
        onPickTree(lossPickValue, { gambleDrawIndices: indices, gambleBust: true });
        return;
      }
      const pick = opts.won ? winPickValue : lossPickValue;
      onPickTree(pick, { gambleDrawIndices: indices });
    },
    [disabled, drawIndices, lossPickValue, onPickTree, resultOutcome, winPickValue]
  );

  useEffect(() => {
    if (playerBust && resultOutcome == null && !submittedRef.current && !disabled) {
      submitOutcome({ bust: true });
    }
  }, [playerBust, resultOutcome, disabled, submitOutcome]);

  const handleHit = useCallback(() => {
    if (!canHit || remainingDeckIndices.length < 1) {
      return;
    }
    const pickIdx = Math.floor(Math.random() * remainingDeckIndices.length);
    const nextDeckIndex = remainingDeckIndices[pickIdx];
    setDrawIndices((prev) => [...prev, nextDeckIndex]);
  }, [canHit, remainingDeckIndices]);

  const handleHold = useCallback(() => {
    if (!canHold || submittedRef.current) {
      return;
    }
    submittedRef.current = true;
    onPickTree(winPickValue, { gambleDrawIndices: [...drawIndices] });
  }, [canHold, drawIndices, onPickTree, winPickValue]);

  const displayGrannyTotal =
    grannyRevealTotal != null
      ? grannyRevealTotal
      : gambleResolution?.grannyTotal != null
        ? gambleResolution.grannyTotal
        : null;

  const renderDeskCards = (indices) =>
    indices.map((deckIndex, handIndex) => {
      const card = deck.find((c) => c.deckIndex === deckIndex);
      if (!card) {
        return null;
      }
      const order = orderById[card.orderId];
      const pos = theGamblePlayerDeskCardPercentPosition(handIndex);
      return (
        <div
          key={`gamble-card-${deckIndex}`}
          className="the-gamble-card-wrap the-gamble-card-wrap--desk"
          style={{ left: pos.left, top: pos.top }}
        >
          {order ? (
            <OrderDesign order={order} count={0} minimal showButtons={false} />
          ) : (
            <div className="the-gamble-loading-card" aria-hidden="true" />
          )}
          <span className="the-gamble-card-cost">({card.cost})</span>
        </div>
      );
    });

  return (
    <div className="the-gamble">
      <div ref={setPlayArenaRef} className="the-gamble-play-arena">
        <img className="the-gamble-bg" src={THE_GAMBLE_BACKGROUND_SRC} alt="" draggable={false} />
        <div className="the-gamble-hud the-gamble-hud--top" data-no-minigame-flashlight="true">
          <h3 className="the-gamble-title">{t('exploreUnknownPage.minigame.theGamble.title')}</h3>
          <p className="the-gamble-explanation">{t('exploreUnknownPage.minigame.theGamble.explanation')}</p>
          <div className="the-gamble-scores">
            <p className="the-gamble-score the-gamble-score--player">
              {t('exploreUnknownPage.minigame.theGamble.playerScore', {
                total: playerTotal,
                target: targetScore,
              })}
            </p>
            <p className="the-gamble-score the-gamble-score--granny">
              {displayGrannyTotal != null
                ? t('exploreUnknownPage.minigame.theGamble.grannyScore', {
                    total: displayGrannyTotal,
                    target: targetScore,
                  })
                : t('exploreUnknownPage.minigame.theGamble.grannyScoreHidden', { target: targetScore })}
            </p>
          </div>
          {orderLoadError ? (
            <p className="the-gamble-explanation" role="alert">
              {orderLoadError}
            </p>
          ) : null}
        </div>

        <div className="the-gamble-desk" aria-label={t('exploreUnknownPage.minigame.theGamble.playerLabel')}>
          {renderDeskCards(drawIndices)}
        </div>

        <div className="the-gamble-dealer-note" data-no-minigame-flashlight="true">
          <p className="the-gamble-hand-label">{t('exploreUnknownPage.minigame.theGamble.grannyLabel')}</p>
          {displayGrannyTotal != null ? (
            <p className="the-gamble-hand-label the-gamble-hand-label--dealer">
              {t('exploreUnknownPage.minigame.theGamble.grannyHeld', { total: displayGrannyTotal })}
            </p>
          ) : (
            <p className="the-gamble-hand-label the-gamble-hand-label--dealer">
              {t('exploreUnknownPage.minigame.theGamble.grannyWaiting')}
            </p>
          )}
        </div>

        {resultOutcome == null ? (
          <div className="the-gamble-actions" data-no-minigame-flashlight="true">
            <button
              type="button"
              className="the-gamble-action-btn"
              disabled={!canHit || ordersLoading}
              onClick={handleHit}
            >
              {t('exploreUnknownPage.minigame.theGamble.hit')}
            </button>
            <button
              type="button"
              className="the-gamble-action-btn the-gamble-action-btn--hold"
              disabled={!canHold || ordersLoading}
              onClick={handleHold}
            >
              {t('exploreUnknownPage.minigame.theGamble.hold')}
            </button>
          </div>
        ) : null}

        {resultOutcome != null ? (
          <div
            className={`the-gamble-hud the-gamble-hud--bottom the-gamble-result-banner the-gamble-result-banner--${resultOutcome}`}
            role="status"
            aria-live="polite"
            data-no-minigame-flashlight="true"
          >
            <p className="the-gamble-result-title">
              {resultOutcome === 'won'
                ? t('exploreUnknownPage.minigame.theGamble.successTitle')
                : t('exploreUnknownPage.minigame.theGamble.failureTitle')}
            </p>
            {gambleResolution ? (
              <p className="the-gamble-result-body">
                {gambleResolution.bust
                  ? t('exploreUnknownPage.minigame.theGamble.failureBodyBust', {
                      total: gambleResolution.playerTotal,
                      target: targetScore,
                    })
                  : displayGrannyTotal != null
                    ? t('exploreUnknownPage.minigame.theGamble.resultCompare', {
                        playerTotal: gambleResolution.playerTotal,
                        grannyTotal: displayGrannyTotal,
                        target: targetScore,
                      })
                    : null}
              </p>
            ) : null}
            {resultOutcome === 'won' && winRewardBodyKey ? (
              <p className="the-gamble-result-body">
                {t(winRewardBodyKey, {
                  percent: winReward?.darknessReductionApplied,
                  xp: winReward?.xpGained,
                  billyBucks: winReward?.billyBucksAmount,
                  health: winReward?.startHealthBonus != null ? winReward.startHealthBonus : 3,
                })}
              </p>
            ) : null}
            {resultOutcome === 'lost' ? (
              <p className="the-gamble-result-body">{t('exploreUnknownPage.minigame.theGamble.failureBodyDarkness')}</p>
            ) : null}
            {typeof onResultProceed === 'function' ? (
              <button
                type="button"
                className={`the-gamble-result-proceed${resultOutcome === 'lost' ? ' the-gamble-result-proceed--lost' : ''}`}
                onClick={() => onResultProceed()}
              >
                {t('exploreUnknownPage.minigame.proceed')}
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
